import CTNBreadcrumb from '@ctn/ui/components/ctn-breadcrumb';
import CTNButton from '@ctn/ui/components/ctn-button';
import { type CTNBreadcrumbLevel } from '@ctn/ui/types/ctn-breadcrumb';
import classNames from 'classnames';
import React, { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Menu from '@/containers/Menu';
import { useDirtyForm } from '@/context/dirtyForm.context';
import { usePageContext } from '@/context/page.context';
import useScrollToTop from '@/hooks/useScrollToTop';

import { ROUTES } from '@/constants/routes.constants';
import { BREAKPOINTS } from '@/constants/viewport.constants';

import Footer from '../Footer';
import HeadPrivate from '../Head/HeadPrivate';

type LayoutProps = {
	readonly children?: ReactNode;
};

export default function LayoutPrivate({ children }: LayoutProps): React.ReactElement {
	const navigate = useNavigate();
	const location = useLocation();

	const idLayoutMain = 'layout-main';
	useScrollToTop(`#${idLayoutMain}`);

	const { checkDirtyForm } = useDirtyForm();
	const { breadcrumb, urlBack, onBackClick, onClickBreadcrumb, scrollTop } = usePageContext();

	const [isShowMenu, setIsShowMenu] = useState(window.innerWidth > BREAKPOINTS.DESKTOP);
	const [isCollapseMenu, setIsCollapseMenu] = useState(false);

	const layoutClassName = classNames('o-layout', 'o-layout--has-menu');

	/** Mapeo para aplicar checkDirtyForm, url no aplica, será siempre por callback */
	const breadcrumbMap: CTNBreadcrumbLevel[] = (breadcrumb ?? []).map(({ label, url, callback }, index) => {
		if ((Boolean(callback) || url) && index !== (breadcrumb ?? []).length - 1) {
			return {
				label,
				callback: () =>
					checkDirtyForm({
						action() {
							onClickBreadcrumb(index);
						},
					}),
			};
		}
		return { label };
	});

	const toggleMenu = () => {
		if (window.innerWidth < BREAKPOINTS.LANDSCAPE) {
			setIsShowMenu(!isShowMenu);
		} else {
			setIsCollapseMenu(!isCollapseMenu);
		}
	};

	// Ocultar o mostrar el menú según el tamaño de la ventana
	useEffect(() => {
		const handleResize = () => {
			setIsShowMenu(window.innerWidth >= BREAKPOINTS.LANDSCAPE);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		scrollTop();
	}, [location.pathname]);

	return (
		<div className={layoutClassName}>
			<HeadPrivate toggleMenu={toggleMenu} />
			<Menu isShowMenu={isShowMenu} isCollapseMenu={isCollapseMenu} onClose={() => setIsShowMenu(!isShowMenu)} />
			<div id={idLayoutMain} className="[ o-layout__main ] [ u-scroll ]">
				<div className="o-wrapper">
					{!location.pathname.includes('home') && 
					!location.pathname.includes('success') && (
						<div className="c-pnp-breadcrumb">
							<CTNBreadcrumb
								className="c-pnp-breadcrumb__nav"
								levels={breadcrumbMap}
								home={{
									label: '',
									callback: () =>
										checkDirtyForm({
											action() {
												navigate(ROUTES.HOME);
											},
										}),
								}}
							/>
							{(Boolean(urlBack?.show) || (breadcrumb ?? []).length > 0) && (
								<CTNButton
									label={urlBack?.label ?? 'Volver'}
									mode="secondary"
									variant="ghost"
									icon={[{ icon: 'arrowLeftCircle', position: 'left' }]}
									className="c-pnp-breadcrumb__btn"
									onClick={() => {
										checkDirtyForm({
											action() {
												// Override del funcionamiento habitual de Volver (naviagate(-1))
												onBackClick();
											},
										});
									}}
								/>
							)}
						</div>
					)}
					<main id="app-container">{children}</main>
					<Footer className="u-mt-32" origin='private' />
				</div>
			</div>
		</div>
	);
}
